import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { UserData } from "app/shared/service/UserService";
import { catchError, Observable, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AtcControllerService {
  private apiUrl: string;
  constructor(private http: HttpClient) {
    this.apiUrl = UserData.backend_url + "/v1";
  }

  public getActiveAirports() {
    const url =
      this.apiUrl +
      "/atc/airports?pageSize=10000&pageNo=0&sortField=airportName&sortDirection=asc&searchField=";
    return this.http.get(url);
  }

  public getAirlineByAirportId(airportId) {
    const url =
      this.apiUrl +
      "/atc/airlines?airportId=" +
      airportId +
      "&pageSize=10000&pageNo=0&sortField=airlineName&sortDirection=asc&searchField=";
    return this.http.get(url);
  }

  public getAircrafts(data) {
    const url =
      this.apiUrl +
      "/atc/aircraft?pageSize=10000&pageNo=0&sortField=aircraftName&sortDirection=asc&searchField=";
    return this.http.post(url, data);
  }

  public getFlightsMasterList(airportId) {
    const url = this.apiUrl + "/flights-master/getFlights?airportId=" + airportId;
    return this.http.get(url);
  }

  // Commands section (SG-8410): airport's phraseology commands with show==1, resolved to
  // category/command names.
  public getPhraseologyExerciseCommands(airportId: string) {
    const url = this.apiUrl + "/phraseology-commands-generated/getExerciseCommands?airportId=" + airportId;
    return this.http.get(url);
  }

  // Still used by the phraseology module (phraseology.component.ts, phraseology-popup
  // .component.ts) — not exclusive to the removed exercise "Commands" section.
  public getCommandCategories() {
    const url = this.apiUrl + "/getCommands";
    return this.http.get(url);
  }

  public getCommands(data, gameId, organizationId) {
    const url =
      this.apiUrl +
      "/organization/" +
      organizationId +
      "/voiceCommand-config?pageSize=100000&pageNo=0&sortField=commandName&sortDirection=asc&gameId=" +
      gameId +
      "&searchField=";
    return this.http.post(url, data);
  }

  public getGameEndMasterConfig(gameId) {
    const url = this.apiUrl + "/gameEnd?missionType=Mission 1&gameId=" + gameId;
    return this.http.get(url);
  }

  public getExercises(data, params = {}) {
    const url = this.apiUrl + "/atc/excercise";
    return this.http.get(url, { params: params });
  }

  public getExerciseById(exerciseId, requestedData) {
    const url =
      this.apiUrl + "/atc?requestedData=" + requestedData + "&exerciseId=" + exerciseId;
    return this.http.get(url);
  }

  public deleteExercise(id) {
    const url = this.apiUrl + "/atc/excercise/" + id;
    return this.http.delete(url);
  }

  public publishExercise(id) {
    const url = this.apiUrl + "/atc/excercise/publish/" + id;
    return this.http.patch(url, {});
  }

  /*public saveExercise(data) {
    const url = this.apiUrl + "/atc/excercise";
    return this.http.post(url, data);
  }*/

  public saveExercise(data: any) {
    const url = this.apiUrl + "/atc/excercise";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  public cloneExercise(data: any) {
    const url = this.apiUrl + "/atc/excercise/clone";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  /*public updateExercise(data) {
    const url = this.apiUrl + "/atc/excercise";
    return this.http.put(url, data);
  }*/

  public updateExercise(data: any) {
    const url = this.apiUrl + "/atc/excercise";
    return this.http.put(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  public getMasterSIDsByRunwayIds(runwayIds: string[]) {
    const params = runwayIds.map(id => `runwayIds=${encodeURIComponent(id)}`).join('&');
    const url = this.apiUrl + `/runway-sid/by-runways?${params}`;
    return this.http.get(url);
  }

  // Missed Approach
  public saveMissedApproach(data: any) {
    const url = this.apiUrl + '/atc/excercise/missed-approach';
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError?.message || backendError || 'Failed to save missed approach'));
      })
    );
  }

  //Runway Tab
  public getRunways(data, airportId) {
    const url =
      this.apiUrl + "/runway/runway-config?pageSize=100&pageNo=0&sortField=airportName&sortDirection=asc&searchField=" + airportId;
    return this.http.post(url, data);
  }

  public saveOrUpdateRunways(data: any) {
    const url = this.apiUrl + "/atc/excercise/runway";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  // Runway SID/STAR (paths) for a given runway end
  public getRunwaySidsByRunway(runwayId: string) {
    const url = this.apiUrl + "/runway-sid/by-runway/" + runwayId;
    return this.http.get(url);
  }

  //Fixes Tab
  public getFixes(data) {
    const url =
      this.apiUrl + "/fixes/fixes-config?pageSize=10000&pageNo=0&sortField=&sortDirection=asc&searchField=";
    return this.http.post(url, data);
  }

  public saveOrUpdateFixes(data: any) {
    const url = this.apiUrl + "/atc/excercise/fix";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  public updateFixName(exerciseId: string, fixName: string, newFixName: string) {

    const payload = {
      exerciseId: exerciseId,
      fixName: fixName,
      newFixName: newFixName
    };

    const url = this.apiUrl + "/atc/fixes";
    return this.http.patch(url, payload).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  // Radials
  public getRadialAirports() {
    const url = this.apiUrl + "/radial/airports";
    return this.http.get(url);
  }

  public getRadialsByAirport(airportID: string) {
    const url = this.apiUrl + "/radial/by-airport?airportID=" + airportID;
    return this.http.get(url);
  }

  public getHoldingPatternsByAirport(airportID: string) {
    const url = this.apiUrl + "/holding-pattern/by-airport?airportID=" + airportID;
    return this.http.get(url);
  }

  //Path Tab
  public saveOrUpdatePath(data: any) {
    const url = this.apiUrl + "/atc/excercise/path";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  // NO-FLY Tab
  public saveOrUpdateObstruction(data: any) {
    const url = this.apiUrl + "/atc/excercise/obstruction";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  public saveOrUpdateZone(data: any) {
    const url = this.apiUrl + "/atc/excercise/zone";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  getTutorial(exerciseId: string) {
    const url =
      this.apiUrl + "/vectra/tutorial?exerciseId=" + exerciseId;
    return this.http.get(url);

    /*const tutorial: any = {
      exerciseId: "689b9b6107211827cfc39cb6",
      exerciseName: "Testing ...",
      content: "Testing Content...."
    };
    return tutorial;*/
  }

  public saveOrUpdateTutorial(data: any) {
    const url = this.apiUrl + "/vectra/tutorial";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError));
      })
    );
  }

  getTutorialContentAsPdf(exerciseId: String): Observable<Blob> {
    return this.http.get(this.apiUrl + "/vectra/tutorial/" + exerciseId + "/contentAsPdf", { responseType: 'blob' });
  }

  public getFixTypes() {
    return this.http.get(this.apiUrl +  "/fixes/fixes-type");
  }

  public getObstructionTypes() {
    return this.http.get(this.apiUrl +  "/atc/obstruction-type");
  }

  // Zone Master
  public getZones(data, searchField) {
    const url =
      this.apiUrl + "/zone/zone-config?pageSize=10000&pageNo=0&sortField=zoneName&sortDirection=asc&status=active&searchField=" + searchField;
    return this.http.post(url, data);
  }

  // Obstruction Master
  public getObstructions(data, searchField) {
    const url =
      this.apiUrl + "/obstruction/obstruction-config?pageSize=10000&pageNo=0&sortField=obstructionName&sortDirection=asc&searchField=" + searchField;
    return this.http.post(url, data);
  }

  // Obstruction Master
  public getHoldingPatterns(data, searchField) {
    const url =
      this.apiUrl + "/holding-pattern/config?pageSize=10000&pageNo=0&sortField=holdName&sortDirection=asc&searchField=" + searchField;
    return this.http.post(url, data);
  }

  // SG-8586 start
  public getFlightPlansByExerciseId(exerciseId: string) {
    const url = this.apiUrl + "/atc/excercise/flight-plans/" + exerciseId;
    return this.http.get(url);
  }

  public saveFlightPlans(data: any) {
    const url = this.apiUrl + "/atc/excercise/flight-plans";
    return this.http.post(url, data).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError?.message || backendError || 'Failed to save flight plans'));
      })
    );
  }

  public deleteFlightPlans(exerciseId: string) {
    const url = this.apiUrl + "/atc/excercise/flight-plans/" + exerciseId;
    return this.http.delete(url).pipe(
      catchError((error) => {
        const backendError = error?.error;
        return throwError(() => new Error(backendError?.message || backendError || 'Failed to delete flight plans'));
      })
    );
  }

  public getExerciseRadials(exerciseId: string) {
    const url = this.apiUrl + `/exercise/${exerciseId}/radials`;
    return this.http.get(url);
  }

  /** ILS categories master list. Records: { ilsCategory }. Same endpoint the Flight Plan Setup screen uses. */
  public getIlsCategories() {
    const url = this.apiUrl + "/flight-plan/ils-category";
    return this.http.get(url);
  }

  /** Full flight-plan config record by id (the list response is a slim record — fields
   *  like startPositionName / eet / eta / airport names only exist on the full record). */
  public getFlightPlanConfigById(id: string) {
    const url = this.apiUrl + "/flight-plan/flight-plan-config";
    return this.http.post(url, { configType: 'get', id });
  }

  public getAllFlightPlanConfigs(searchField: string = '', extraBody: any = {}) {
    const url = this.apiUrl + `/flight-plan/flight-plan-config?pageNo=0&pageSize=10000&sortField=&sortDirection=&searchField=${encodeURIComponent(searchField)}`;
    return this.http.post(url, { configType: 'get', ...extraBody });
  }
  // SG-8586 end
}
